import React, { useState, useEffect, useCallback } from 'react';
import {
  Container, Typography, Box, Alert, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, IconButton, Chip, Dialog, DialogTitle, DialogContent,
  DialogContentText, DialogActions, Button, Collapse, List,
  ListItem, ListItemText, ListItemSecondaryAction, Tooltip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { useNavigate } from 'react-router-dom';
import { getAdminEntraUsers, removeUserFromTrip, deleteUserFully } from '../services/api';

function UserRow({ user, onRemoveFromTrip, onDeleteUser }) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <TableRow hover sx={{ '& > *': { borderBottom: 'unset' } }}>
        <TableCell sx={{ width: 48, pr: 0 }}>
          <IconButton size="small" onClick={() => setOpen((o) => !o)}>
            {open ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
          </IconButton>
        </TableCell>
        <TableCell>
          {user.username || <em style={{ opacity: 0.45 }}>—</em>}
        </TableCell>
        <TableCell>{user.displayName || <em style={{ opacity: 0.45 }}>—</em>}</TableCell>
        <TableCell>
          {user.isCustomAuth
            ? <Chip label="Custom Auth" size="small" color="warning" />
            : <Chip label="Entra ID" size="small" color="info" />}
        </TableCell>
        <TableCell>
          {user.isCustomAuth ? (
            <Chip label="UUID (no Entra)" size="small" variant="outlined" />
          ) : user.entraProfile ? (
            <Chip label="Resolved" size="small" color="success" />
          ) : (
            <Chip label="Not found" size="small" color="error" variant="outlined" />
          )}
        </TableCell>
        <TableCell>
          {user.role === 'admin'
            ? <Chip label="Admin" size="small" color="primary" />
            : <Chip label="User" size="small" variant="outlined" />}
        </TableCell>
        <TableCell>{user.trips.length}</TableCell>
        <TableCell align="right">
          <Tooltip title="Delete user (removes from all trips, deletes Entra account if applicable)">
            <IconButton color="error" size="small" onClick={() => onDeleteUser(user)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell colSpan={8} sx={{ pb: 0, pt: 0 }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ m: 2, ml: 7 }}>
              {user.entraProfile && (
                <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                  Entra: <strong>{user.entraProfile.displayName}</strong>
                  {(user.entraProfile.mail || user.entraProfile.userPrincipalName) && (
                    <> &middot; {user.entraProfile.mail || user.entraProfile.userPrincipalName}</>
                  )}
                </Typography>
              )}

              <Typography variant="subtitle2" gutterBottom>
                Trip memberships ({user.trips.length})
              </Typography>

              {user.trips.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Not a member of any trips.
                </Typography>
              ) : (
                <List dense disablePadding>
                  {user.trips.map((trip) => (
                    <ListItem key={trip.tripId} divider sx={{ pl: 0 }}>
                      <ListItemText
                        primary={trip.name}
                        secondary={trip.tripId}
                        primaryTypographyProps={{ variant: 'body2' }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                      <ListItemSecondaryAction>
                        <Tooltip title="Remove from this trip">
                          <IconButton
                            edge="end"
                            size="small"
                            color="warning"
                            onClick={() => onRemoveFromTrip(user, trip)}
                          >
                            <RemoveCircleOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [removeTarget, setRemoveTarget] = useState(null); // { user, trip }
  const [acting, setActing] = useState(false);
  const navigate = useNavigate();

  const loadUsers = useCallback(async () => {
    try {
      const { data } = await getAdminEntraUsers();
      setUsers([...data].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
    } catch (err) {
      if (err.response?.status === 403) {
        navigate('/');
        return;
      }
      setError('Failed to load users.');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const handleRemoveFromTrip = async () => {
    if (!removeTarget) return;
    setActing(true);
    try {
      await removeUserFromTrip(removeTarget.user.userId, removeTarget.trip.tripId);
      setUsers((prev) =>
        prev.map((u) =>
          u.userId === removeTarget.user.userId
            ? { ...u, trips: u.trips.filter((t) => t.tripId !== removeTarget.trip.tripId) }
            : u,
        ),
      );
      setRemoveTarget(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to remove user from trip.');
    } finally {
      setActing(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!deleteTarget) return;
    setActing(true);
    try {
      await deleteUserFully(deleteTarget.userId);
      setUsers((prev) => prev.filter((u) => u.userId !== deleteTarget.userId));
      setDeleteTarget(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete user.');
    } finally {
      setActing(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={6}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 6 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight="bold">User Management</Typography>
        <Chip label={`${users.length} users`} />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 48 }} />
              <TableCell><strong>Username</strong></TableCell>
              <TableCell><strong>Display Name</strong></TableCell>
              <TableCell><strong>Auth Type</strong></TableCell>
              <TableCell><strong>Entra Status</strong></TableCell>
              <TableCell><strong>Role</strong></TableCell>
              <TableCell><strong>Trips</strong></TableCell>
              <TableCell align="right"><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user) => (
              <UserRow
                key={user.userId}
                user={user}
                onRemoveFromTrip={(u, trip) => setRemoveTarget({ user: u, trip })}
                onDeleteUser={setDeleteTarget}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Remove from trip confirmation */}
      <Dialog open={!!removeTarget} onClose={() => setRemoveTarget(null)}>
        <DialogTitle>Remove from Trip</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Remove <strong>{removeTarget?.user?.displayName || removeTarget?.user?.username}</strong> from{' '}
            <strong>{removeTarget?.trip?.name}</strong>? They will lose access to this trip.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoveTarget(null)}>Cancel</Button>
          <Button color="warning" variant="contained" onClick={handleRemoveFromTrip} disabled={acting}>
            {acting ? 'Removing...' : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Full delete confirmation */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete User</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Permanently delete <strong>{deleteTarget?.displayName || deleteTarget?.username}</strong>? This will:
          </DialogContentText>
          <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
            <li>Remove them from all trips</li>
            {deleteTarget && !deleteTarget.isCustomAuth && (
              <li>Delete their Entra ID account</li>
            )}
            <li>Delete their profile from the database</li>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDeleteUser} disabled={acting}>
            {acting ? 'Deleting...' : 'Delete Permanently'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
